import { Timestamp } from 'firebase/firestore';

export interface OptimizedContent {
  title: string;
  description: string;
  bulletPoints: string[];
  metaDescription?: string;
  pageTitle?: string;
  productType?: string;
  productCategory?: string;
  lastOptimized: Date | Timestamp | null;
}

export interface Optimizations {
  shopify?: OptimizedContent;
  amazon?: OptimizedContent;
  googleShopping?: OptimizedContent;
}

export interface ProductImage {
  url: string;
  thumbnailUrl: string;
  position: number;
}

export interface BasicInfo {
  sku: string;
  asin?: string;
  title: string;
  description: string;
  metaDescription?: string;
  pageTitle?: string;
  bulletPoints: string[];
  categories: string[];
  imageUrl?: string;
}

export interface Links {
  shopify?: string;
  amazon?: string;
}

export interface Timestamps {
  created: Date;
  updated: Date;
  imported: Date;
}

export interface PublishTimestamps {
  shopify?: Date | Timestamp | null;
  amazon?: Date | Timestamp | null;
  googleShopping?: Date | Timestamp | null;
}

// Legacy fields for backward compatibility during migration
interface LegacyFields {
  productId?: string;
  title?: string;
  shopifyStore?: string;
  isImported?: boolean;
  optimizedContent?: OptimizedContent;
  googleOptimizedContent?: OptimizedContent;
  amazonOptimizedContent?: OptimizedContent;
  thumb1?: string;
  image1?: string;
  shopifyLink?: string;
  amazonLink?: string;
  jlsNumber?: string;
}

export interface Product extends LegacyFields {
  id: string;
  source: 'Shopify' | 'Amazon' | 'Google Shopping';
  basicInfo: BasicInfo;
  links: Links;
  images: ProductImage[];
  optimizations: Optimizations;
  status: 'Active' | 'Inactive' | 'Error' | 'Needs Review';
  storeName: string;
  timestamps: Timestamps;
  publishTimestamps?: PublishTimestamps;
}

// Helper function to format publish date
export function formatPublishDate(date: Date | Timestamp | undefined | null): string {
  if (!date) return 'Not published';
  
  const dateObj = date instanceof Date ? date : 
                 'toDate' in date ? date.toDate() : 
                 new Date(date);
  
  if (isNaN(dateObj.getTime())) return 'Not published';
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Helper function to check if a product has been optimized for any marketplace
export function isProductOptimized(product: Product): boolean {
  // Check both new and legacy optimization fields
  return Boolean(
    product.optimizations?.shopify?.title ||
    product.optimizations?.amazon?.title ||
    product.optimizations?.googleShopping?.title ||
    product.optimizedContent?.title ||
    product.googleOptimizedContent?.title ||
    product.amazonOptimizedContent?.title
  );
}

// Helper function to get the most recent optimization date
export function getLastOptimizationDate(product: Product): Date | null {
  const dates = [
    product.optimizations?.shopify?.lastOptimized,
    product.optimizations?.amazon?.lastOptimized,
    product.optimizations?.googleShopping?.lastOptimized,
    product.optimizedContent?.lastOptimized,
    product.googleOptimizedContent?.lastOptimized,
    product.amazonOptimizedContent?.lastOptimized
  ].filter(Boolean) as Date[];

  return dates.length > 0 
    ? new Date(Math.max(...dates.map(d => d.getTime())))
    : null;
}

// Helper function to get optimization rate for a marketplace
export function getMarketplaceOptimizationRate(products: Product[], marketplace: keyof Optimizations): number {
  const totalProducts = products.length;
  if (totalProducts === 0) return 0;

  const optimizedCount = products.filter(p => {
    if (marketplace === 'shopify') {
      return p.optimizations?.shopify?.title || p.optimizedContent?.title;
    } else if (marketplace === 'amazon') {
      return p.optimizations?.amazon?.title || p.amazonOptimizedContent?.title;
    } else if (marketplace === 'googleShopping') {
      return p.optimizations?.googleShopping?.title || p.googleOptimizedContent?.title;
    }
    return false;
  }).length;

  return (optimizedCount / totalProducts) * 100;
}

// Helper function to format optimization date
export function formatOptimizationDate(date: Date | undefined | null): string {
  if (!date) return 'Not optimized';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Helper function to get product title (handles both new and legacy schemas)
export function getProductTitle(product: Product): string {
  return product.basicInfo?.title || product.title || '';
}

// Helper function to get product store name (handles both new and legacy schemas)
export function getProductStore(product: Product): string {
  return product.storeName || product.shopifyStore || '';
}

// Helper function to get product ID (handles both new and legacy schemas)
export function getProductId(product: Product): string {
  return product.id || product.productId || '';
}